import { FC, useCallback, useState } from 'react';
import {
  TrainingRecord,
  ProgressStatus,
  DownloadStatus,
  DownloadErrorCodes,
} from '@taleemabad/db';
import {
  ProgressWithLabel,
  StyledDiv,
  Typography,
  TypographyVariant,
  Icon as IconComponent,
  Menu,
  List,
  Spacing,
  IconText,
  ProgressListCard,
} from '@taleemabad/dsm/components';
import { TrainingStatus } from '@taleemabad/shared';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { useNetworkStatus } from '@taleemabad/shared';
import { Capacitor } from '@capacitor/core';

const TrainingCardListWrapper = styled.div`
  display: grid;
  gap: var(--rk-spacing-xl);
  grid-template-columns: repeat(3, 1fr);
  ${mq(BreakpointSizes.xs)} {
    grid-template-columns: repeat(1, 1fr);
  }
  ${mq(BreakpointSizes.sm)} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

interface TrainingCardListProps {
  trainings: TrainingRecord[];
  downloadUIState: boolean;
  trainingDownloadEnabled?: boolean;
  onTrainingCardClick: (item: TrainingRecord) => void;
  onTrainingRetryDownloadClick?: (item: TrainingRecord) => void;
  onTrainingDownloadClick?: (item: TrainingRecord) => void;
  onTrainingDeleteClick?: (item: TrainingRecord) => void;
}

enum TrainingAction {
  RemoveDownload = 'removeDownload',
}

const menuData = [
  { id: 1, name: 'Remove download', value: 'removeDownload', icon: 'delete' },
];

export const TrainingCardList: FC<TrainingCardListProps> = ({
  trainings,
  trainingDownloadEnabled = false,
  onTrainingCardClick,
  onTrainingDownloadClick,
  onTrainingRetryDownloadClick,
  onTrainingDeleteClick,
}) => {
  const [openMenu, setOpenMenu] = useState<null | number>(null);
  const { isConnected } = useNetworkStatus();
  const getInProgressTraining = trainings.find(
    (training) =>
      training.trainingStatus === null ||
      training.trainingStatus === ProgressStatus.InProgress,
  );
  const getProgressStatus = (status: ProgressStatus | null, id: number) => {
    if (getInProgressTraining && getInProgressTraining.id === id) {
      return TrainingStatus.InProgress;
    }
    if (status === ProgressStatus.InProgress) {
      return TrainingStatus.InProgress;
    }
    if (status === ProgressStatus.Completed) {
      return TrainingStatus.Completed;
    }
    return TrainingStatus.Locked;
  };

  const setTrailingIcon = useCallback((training: TrainingRecord) => {
    if (!Capacitor.isNativePlatform()) return {};
    if (!trainingDownloadEnabled) return {};
    const downloadStatus = training?.mediaAsset?.download?.status;
    const downloadProgress = training?.mediaAsset?.download
      ? Math.floor(
          (training?.mediaAsset?.download.downloaded /
            training?.mediaAsset?.download.total) *
            100,
        )
      : 0;

    let trailingIconProps = {};
    // Dynamically handle all downloadStatus types
    if (
      downloadStatus === DownloadStatus.Downloading ||
      downloadStatus === DownloadStatus.Added ||
      downloadStatus === DownloadStatus.Queued
    ) {
      trailingIconProps = {
        trailingComponent: (
          <ProgressWithLabel
            value={downloadProgress}
            text={`${downloadProgress}%`}
            size="sm"
          />
        ),
      };
    } else if (downloadStatus === DownloadStatus.Completed) {
      trailingIconProps = {
        trailingIcon: {
          type: 'icon',
          source: 'more_vert',
          direction: 'trailing',
          onClick: () => {
            setOpenMenu((prevOpenMenu) =>
              prevOpenMenu === training.id ? null : (training.id as number),
            );
          },
        },
      };
    } else if (downloadStatus === DownloadStatus.Failed) {
      trailingIconProps = {
        trailingComponent: (
          <IconComponent
            name={'sync'}
            color="--md-sys-color-schemes-error"
            onClick={() => {
              onTrainingRetryDownloadClick?.(training);
            }}
          />
        ),
      };
    } else if (!downloadStatus) {
      trailingIconProps = {
        trailingIcon: {
          type: 'icon',
          source: 'download',
          direction: 'trailing',
          cursor: 'pointer',
          onClick: () => {
            onTrainingDownloadClick?.(training);
          },
        },
      };
    } else {
      // Handle other statuses dynamically here if needed
      trailingIconProps = {
        trailingIcon: {
          type: 'icon',
          source: 'more_vert',
          direction: 'trailing',
          cursor: 'pointer',
          onClick: () => {},
        },
      };
    }

    return trailingIconProps;
  }, []);

  return (
    <TrainingCardListWrapper>
      {trainings?.map((training: TrainingRecord, index) => (
        <div
          key={training.title}
          data-cy="teacher-training-trainings-list"
          style={{ position: 'relative' }}
        >
          <ProgressListCard
            cardOnClick={() => onTrainingCardClick(training)}
            progressPercentage={0}
            levelName={training.title}
            levelStatus={getProgressStatus(
              training.trainingStatus,
              training.id,
            )}
            dataCy="training-card"
          />
          {((training?.mediaAsset?.download?.status ===
            DownloadStatus.Downloading ||
            training?.mediaAsset?.download?.status === DownloadStatus.Added ||
            training?.mediaAsset?.download?.status === DownloadStatus.Queued) &&
            !isConnected) ||
          training?.mediaAsset?.download?.status === DownloadStatus.Failed ? (
            <Spacing marginTop="lg">
              <IconText
                textVariant="textBodySmallSchemesError"
                iconColor="--md-sys-color-schemes-error"
                icon="error"
                text={
                  training?.mediaAsset.download?.status ===
                    DownloadStatus.Downloading ||
                  training?.mediaAsset.download?.status ===
                    DownloadStatus.Added ||
                  training?.mediaAsset.download?.status ===
                    DownloadStatus.Queued
                    ? 'Internet disconnected. Connect to continue.'
                    : training?.mediaAsset.download?.error ===
                        DownloadErrorCodes.fileAllocationFailed
                      ? 'Low memory. clear memory and try again.'
                      : 'Training failed to download. Try again.'
                }
              />
            </Spacing>
          ) : null}
          <Menu
            positioning="absolute"
            open={openMenu === training.id}
            menuCorner="end-end"
            anchorCorner="end-end"
            menuItems={menuData}
            onSelectedItem={(item) => {
              setOpenMenu(null);
              if (item.value === TrainingAction.RemoveDownload) {
                onTrainingDeleteClick?.(training);
              }
            }}
            onClose={() => setOpenMenu(null)}
          />
        </div>
      ))}
    </TrainingCardListWrapper>
  );
};
